import React, { useCallback, useMemo, useState } from "react";
import { Alert as MuiAlert, Snackbar as MuiSnackbar } from "@mui/material";
import { Blocks } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import { useAppSelector } from "@/state/hooks";
import { FeatureFlag } from "@/features";
import {
    verifyAtlassianDomain, initiateAtlassianOAuth, deleteAtlassianCredentials
} from "@/api/integrations/atlassian";
import { initiateGitLabOAuth } from "@/api/integrations/gitlab";
import { initiateBitbucketOAuth } from "@/api/integrations/bitbucket";
import { initiateAzureDevOpsOAuth, deleteAzureDevOpsCredentials } from "@/api/integrations/azureDevOps";
import { initiateGitHubOAuth } from "@/api/integrations/github";
import { disconnectGitProvider, type IntegrationLinkDto, type ProviderId } from "@/api/integrations/links";
import { openOAuthPopup } from "@/utils/oauthPopup";
import {
    LinkProductDialog, type ProductOption, type TrackerLinkInfo
} from "./LinkProductDialog";
import { ProviderCard } from "./ProviderCard";
import { UnlinkConfirmDialog } from "./UnlinkConfirmDialog";
import { useIntegrationsData, type ProviderState, type SourceRow } from "./useIntegrationsData";
import "./integrations.css";
import { NavigationBackButton } from "../NavigationBackButton";

interface IntegrationsProps {
    currentView: string;
    onViewChange: (view: string) => void;
    onAIChatToggle: () => void;
    onJiraSyncOpen?: (context: "features" | "initiatives" | "integrations") => void;
    refreshSignal?: number;
    /** Shown only when Integrations was opened from the product's Context Mesh. */
    onBackToContextMesh?: () => void;
}

interface PendingUnlink {
    provider: ProviderId;
    row: SourceRow;
    link: IntegrationLinkDto;
}

const collapseKey = (id: ProviderId) => `integrations.collapsed.${id}`;

const IntegrationsPage: React.FC<IntegrationsProps> = ({ refreshSignal = 0, onBackToContextMesh }) => {
    const { getToken } = useAuth();
    const userId = useAppSelector(state => state.userProfiles.userProfile?.userid);
    const products = useAppSelector(state => state.projects.projects);
    const { providers, loading, error, refresh, linkProduct, unlink, syncLink } = useIntegrationsData(refreshSignal);

    const productOptions = useMemo<ProductOption[]>(
        () => (products || []).map(product => ({
            projectid: product.project_id,
            projectname: product.project_name
        })),
        [products]
    );

    const productNameById = useMemo(
        () => new Map(productOptions.map(product => [
            product.projectid,
            product.projectname || `Product ${product.projectid}`
        ])),
        [productOptions]);

    const trackerLinkByProject = useMemo(() => {
        const result = new Map<number, TrackerLinkInfo>();
        for (const provider of providers) {
            if (provider.meta.kind !== "tracker") continue;
            for (const row of provider.rows) {
                for (const link of row.links) {
                    if (!result.has(link.projectId)) {
                        result.set(link.projectId, {
                            providerId: provider.meta.id,
                            providerLabel: provider.meta.name,
                            externalName: row.externalName
                        });
                    }
                }
            }
        }
        return result;
    }, [providers]);

    const gitLinkByProject = useMemo(() => {
        const result = new Map<number, TrackerLinkInfo>();
        for (const provider of providers) {
            if (provider.meta.kind !== "git") continue;
            for (const row of provider.rows) {
                for (const link of row.links) {
                    if (!result.has(link.projectId)) {
                        result.set(link.projectId, {
                            providerId: provider.meta.id,
                            providerLabel: provider.meta.name,
                            externalName: row.externalName
                        });
                    }
                }
            }
        }
        return result;
    }, [providers]);

    const [syncingIds, setSyncingIds] = useState<Set<number>>(() => new Set());
    const [resyncingProviderIds, setResyncingProviderIds] = useState<Set<ProviderId>>(() => new Set());
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {};
        for (const id of ["jira", "github", "azure-devops", "gitlab", "bitbucket"] as ProviderId[]) {
            initial[id] = window.localStorage.getItem(collapseKey(id)) === "1";
        }
        return initial;
    });
    const [pendingLinkProviderId, setPendingLinkProviderId] = useState<ProviderId | null>(null);
    const [linking, setLinking] = useState(false);
    const [pendingUnlink, setPendingUnlink] = useState<PendingUnlink | null>(null);
    const [unlinking, setUnlinking] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" | "warning" }>(
        { open: false, message: "", severity: "success" });

    const notify = useCallback((message: string, severity: "success" | "error" | "info" | "warning" = "success") => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const toggleCollapsed = useCallback((id: ProviderId) => {
        setCollapsed(previous => {
            const next = { ...previous, [id]: !previous[id] };
            window.localStorage.setItem(collapseKey(id), next[id] ? "1" : "0");
            return next;
        });
    }, []);

    const startOAuth = useCallback(async (state: ProviderState, configValue: string) => {
        try {
            const token = await getToken();
            if (!token) throw new Error("Authentication token not available");
            if (!userId) throw new Error("User ID not available");
            let result: { authUrl?: string; authorizationUrl?: string };
            switch (state.meta.id) {
                case "jira": {
                    const domain = configValue || state.siteOrOrg;
                    if (!domain) throw new Error("Please enter your Atlassian domain");
                    const verified = await verifyAtlassianDomain(domain, token);
                    if (!verified.cloudId) throw new Error(verified.error || "Failed to verify Atlassian domain");
                    result = await initiateAtlassianOAuth({ userId, domain, cloudId: verified.cloudId }, token);
                    break;
                }
                case "azure-devops": {
                    const source = configValue || state.organization || "";
                    if (!source) throw new Error("Please enter your Azure DevOps organization URL first (e.g., https://dev.azure.com/your-org)");
                    const match = source.match(/dev\.azure\.com\/([^/]+)/);
                    result = await initiateAzureDevOpsOAuth({ userId, organization: match ? match[1] : source }, token);
                    break;
                }
                case "gitlab":
                    result = await initiateGitLabOAuth({ userId, gitlabUrl: configValue || undefined }, token);
                    break;
                case "bitbucket":
                    result = await initiateBitbucketOAuth({ userId, workspace: configValue || undefined }, token);
                    break;
                case "github":
                    result = await initiateGitHubOAuth({ userId }, token);
                    break;
            }
            const authUrl = result.authUrl || result.authorizationUrl;
            if (!authUrl) throw new Error("No authorization URL received from server");
            const popup = openOAuthPopup(authUrl, `${state.meta.name} OAuth`);
            notify("Please complete the OAuth authorization in the popup window.", "info");
            if (popup) {
                const timer = window.setInterval(() => {
                    if (popup.closed) {
                        window.clearInterval(timer);
                        void refresh();
                    }
                }, 1000);
            }
        } catch (e) {
            notify(e instanceof Error ? e.message : "Connection failed", "error");
        }
    }, [getToken, notify, refresh, userId]);

    const disconnect = useCallback(async (state: ProviderState) => {
        try {
            const token = await getToken();
            if (!token) throw new Error("Authentication token not available");
            switch (state.meta.id) {
                case "jira": await deleteAtlassianCredentials(token); break;
                case "azure-devops": await deleteAzureDevOpsCredentials(token); break;
                default: await disconnectGitProvider(state.meta.id as "github" | "gitlab" | "bitbucket", token);
            }
            notify(`${state.meta.name} disconnected`);
            await refresh();
        } catch (e) {
            notify(e instanceof Error ? e.message : "Disconnect failed", "error");
        }
    }, [getToken, notify, refresh]);

    const handleLinkProduct = useCallback(async (
        state: ProviderState,
        row: SourceRow,
        projectId: number
    ) => {
        if (linking) return;
        setLinking(true);
        try {
            const response = await linkProduct(state.meta.id, row, projectId);
            setPendingLinkProviderId(null);
            const productName = productNameById.get(projectId) || `Product ${projectId}`;
            const createdLink = response.links.find(link => link.projectId === projectId);
            if (!createdLink) {
                throw new Error("The connection was created, but its initial sync could not be started.");
            }

            setSyncingIds(previous => new Set(previous).add(createdLink.id));
            try {
                const syncResponse = await syncLink(createdLink.id);
                if (syncResponse.context_mesh_refresh?.scheduled === false) {
                    notify(
                        `${productName} linked and synced with ${row.externalName}. ${syncResponse.warning || "Context Mesh refresh needs attention."} Request ID: ${syncResponse.context_mesh_refresh.request_id}`,
                        "warning"
                    );
                } else {
                    notify(`${productName} linked and synced with ${row.externalName}`);
                }
            } catch (e) {
                const message = e instanceof Error ? e.message : "Synchronization failed";
                notify(`${productName} was linked to ${row.externalName}, but its initial sync failed: ${message}`, "error");
            } finally {
                setSyncingIds(previous => {
                    const next = new Set(previous);
                    next.delete(createdLink.id);
                    return next;
                });
            }
        } catch (e) {
            notify(e instanceof Error ? e.message : "Linking failed", "error");
        } finally {
            setLinking(false);
        }
    }, [linkProduct, linking, notify, productNameById, syncLink]);

    const confirmUnlink = useCallback(async () => {
        if (!pendingUnlink || unlinking) return;
        const { row, link } = pendingUnlink;
        setUnlinking(true);
        try {
            await unlink(link.id);
            setPendingUnlink(null);
            const productName = productNameById.get(link.projectId) || `Product ${link.projectId}`;
            notify(`${productName} disconnected from ${row.externalName}`);
        } catch (e) {
            notify(e instanceof Error ? e.message : "Unlink failed", "error");
        } finally {
            setUnlinking(false);
        }
    }, [notify, pendingUnlink, productNameById, unlink, unlinking]);

    const handleSync = useCallback(async (link: IntegrationLinkDto) => {
        if (syncingIds.has(link.id)) return;
        setSyncingIds(previous => new Set(previous).add(link.id));
        try {
            const response = await syncLink(link.id);
            const productName = productNameById.get(link.projectId) || `Product ${link.projectId}`;
            if (response.context_mesh_refresh?.scheduled === false) {
                notify(
                    `${productName} synced. ${response.warning || "Context Mesh refresh needs attention."} Request ID: ${response.context_mesh_refresh.request_id}`,
                    "warning"
                );
            } else {
                notify(`${productName} synced`);
            }
        } catch (e) {
            notify(e instanceof Error ? e.message : "Synchronization failed", "error");
        } finally {
            setSyncingIds(previous => {
                const next = new Set(previous);
                next.delete(link.id);
                return next;
            });
        }
    }, [notify, productNameById, syncLink, syncingIds]);

    const handleProviderResync = useCallback(async (state: ProviderState) => {
        if (resyncingProviderIds.has(state.meta.id)) return;
        const links = state.rows.flatMap(row => row.links);
        if (links.length === 0 || links.some(link => syncingIds.has(link.id))) return;

        const linkIds = new Set(links.map(link => link.id));
        setResyncingProviderIds(previous => new Set(previous).add(state.meta.id));
        setSyncingIds(previous => new Set([...previous, ...linkIds]));

        let succeeded = 0;
        let failed = 0;
        let contextMeshWarnings = 0;
        try {
            for (const link of links) {
                try {
                    const response = await syncLink(link.id);
                    succeeded += 1;
                    if (response.context_mesh_refresh?.scheduled === false) {
                        contextMeshWarnings += 1;
                    }
                } catch {
                    failed += 1;
                }
            }

            if (failed === 0 && contextMeshWarnings === 0) {
                notify(`${state.meta.name}: ${succeeded} product connection${succeeded === 1 ? "" : "s"} synced`);
            } else if (failed === 0) {
                const contextMeshMessage = contextMeshWarnings === 1
                    ? "the Context Mesh refresh could not be scheduled."
                    : `${contextMeshWarnings} Context Mesh refresh requests could not be scheduled.`;
                notify(
                    `${state.meta.name}: ${succeeded} product connection${succeeded === 1 ? "" : "s"} synced, but ${contextMeshMessage}`,
                    "warning"
                );
            } else {
                notify(
                    `${state.meta.name}: ${succeeded} synced, ${failed} failed. Retry failed connections individually.`,
                    succeeded > 0 ? "warning" : "error"
                );
            }
        } finally {
            setSyncingIds(previous => {
                const next = new Set(previous);
                linkIds.forEach(id => next.delete(id));
                return next;
            });
            setResyncingProviderIds(previous => {
                const next = new Set(previous);
                next.delete(state.meta.id);
                return next;
            });
        }
    }, [notify, resyncingProviderIds, syncLink, syncingIds]);

    const connectedProductCount = new Set(
        providers.flatMap(provider =>
            provider.rows.flatMap(row => row.links.map(link => link.projectId)))
    ).size;
    const linkedSourceCount = providers.reduce(
        (count, provider) => count + provider.rows.filter(row => row.links.length > 0).length,
        0
    );
    const pendingProvider = pendingUnlink
        ? providers.find(state => state.meta.id === pendingUnlink.provider)?.meta ?? null
        : null;
    const pendingLinkProvider = pendingLinkProviderId
        ? providers.find(state => state.meta.id === pendingLinkProviderId) ?? null
        : null;

    return (
        <div className="il-root" style={{
            width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0
        }}>
            <header style={{
                flex: "0 0 auto", height: 64, padding: "0 32px", background: "#eef2fb",
                borderBottom: "1px solid var(--divider)", display: "flex", alignItems: "center",
                gap: 14, position: "sticky", top: 0, zIndex: 1100
            }}>
                {onBackToContextMesh && (
                    <NavigationBackButton
                        label="Back to Context Mesh"
                        onClick={onBackToContextMesh}
                        description="Return to the product Context Mesh"
                        bridgeId="btn.back-to-context-mesh"
                    />
                )}
                <span style={{ fontSize: 18, fontWeight: 600, color: "var(--fg1)" }}>Integrations</span>
                <span style={{ width: 1, height: 18, background: "var(--border-strong)" }} />
                <span style={{ fontSize: 13.5, color: "var(--fg3)" }}>
                    Connect external tools and services to synchronize product data and to create a Context Mesh
                </span>
            </header>
            <main style={{ flex: 1, overflowY: "auto", padding: "28px 32px 36px", background: "var(--bg)" }}>
                <div style={{
                    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                    gap: 20, marginBottom: 22
                }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
                        <span style={{
                            width: 34, height: 34, borderRadius: 9, background: "var(--primary-soft)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "var(--primary)", flex: "0 0 auto"
                        }}>
                            <Blocks size={19} strokeWidth={1.6} />
                        </span>
                        <div>
                            <div style={{ fontSize: 18, fontWeight: 600, color: "var(--fg1)", letterSpacing: "-0.01em" }}>
                                Platform Integrations
                            </div>
                            <div style={{ fontSize: 13.5, color: "var(--fg3)", marginTop: 2 }}>
                                Connect development &amp; collaboration platforms, then link their projects
                            </div>
                        </div>
                    </div>
                    <span style={{
                        fontSize: 12, fontWeight: 600, color: "var(--fg3)", background: "var(--bg-subtle)",
                        border: "1px solid var(--border)", padding: "6px 12px", borderRadius: 999,
                        flex: "0 0 auto"
                    }}>
                        {`${connectedProductCount} product${connectedProductCount === 1 ? "" : "s"} connected · ${linkedSourceCount} source${linkedSourceCount === 1 ? "" : "s"}`}
                    </span>
                </div>

                {error && (
                    <MuiAlert severity="error" sx={{ mb: 2 }} action={
                        <button type="button" onClick={() => void refresh()}
                            style={{ border: "none", background: "transparent", cursor: "pointer", fontWeight: 600 }}>
                            Retry
                        </button>
                    }>
                        {error}
                    </MuiAlert>
                )}
                {loading && providers.length === 0 && (
                    <div style={{ fontSize: 13, color: "var(--fg3)" }}>Loading integrations…</div>
                )}

                {providers.map(state => (
                    <FeatureFlag key={state.meta.id} name={state.meta.flagName}>
                        <ProviderCard
                            state={state}
                            productNameById={productNameById}
                            syncingIds={syncingIds}
                            pendingUnlinkId={pendingUnlink?.link.id ?? null}
                            collapsed={Boolean(collapsed[state.meta.id])}
                            linking={linking && pendingLinkProviderId === state.meta.id}
                            resyncing={resyncingProviderIds.has(state.meta.id)}
                            onConnect={value => void startOAuth(state, value)}
                            onLinkProduct={() => setPendingLinkProviderId(state.meta.id)}
                            onToggleCollapsed={() => toggleCollapsed(state.meta.id)}
                            onManageResync={() => void handleProviderResync(state)}
                            onRetryListing={() => void refresh()}
                            onManageReconnect={() => void startOAuth(state, "")}
                            onManageDisconnect={() => void disconnect(state)}
                            onUnlink={(row, link) => setPendingUnlink({ provider: state.meta.id, row, link })}
                            onSync={(_row, link) => void handleSync(link)}
                        />
                    </FeatureFlag>
                ))}
            </main>

            {pendingLinkProvider && (
                <LinkProductDialog
                    open
                    state={pendingLinkProvider}
                    products={productOptions}
                    trackerLinkByProject={trackerLinkByProject}
                    gitLinkByProject={gitLinkByProject}
                    linking={linking}
                    onConfirm={(row, projectId) => void handleLinkProduct(pendingLinkProvider, row, projectId)}
                    onCancel={() => { if (!linking) setPendingLinkProviderId(null); }}
                />
            )}

            <UnlinkConfirmDialog
                open={Boolean(pendingUnlink)}
                productName={pendingUnlink
                    ? (productNameById.get(pendingUnlink.link.projectId) || `Product ${pendingUnlink.link.projectId}`)
                    : ""}
                providerName={pendingProvider?.name || ""}
                removing={unlinking}
                onConfirm={() => void confirmUnlink()}
                onCancel={() => { if (!unlinking) setPendingUnlink(null); }}
            />

            <MuiSnackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <MuiAlert severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                    {snackbar.message}
                </MuiAlert>
            </MuiSnackbar>
        </div>
    );
};

export default IntegrationsPage;
